import { type ICol, type ISection } from "src/interfaces";

import { OVERLAY_PREF } from "../constants";
import { isBlankRow } from "./blankRowUtils";

export interface CollectActiveCellUuidsOptions {
  /** Skip blank rows when a section has more than one row (continuous-entry behaviour). */
  excludeBlankRows?: boolean;
  /** When set, cells/sections hidden via showContent are excluded from validation. */
  showContent?: Record<string, boolean>;
}

type HideableElement = {
  feature?: string;
  onClass?: string;
  hidden?: boolean;
};

const isContentVisible = (
  className: string | undefined,
  showContent: Record<string, boolean> | undefined,
): boolean => {
  if (!className || !showContent) return false;
  return Object.entries(showContent).some(
    ([key, value]) => key.includes(className) && value === true,
  );
};

const shouldHideElement = (
  element: HideableElement,
  showContent?: Record<string, boolean>,
): boolean => {
  if (!showContent) return false;

  if (element.feature === "showContent" && element.onClass) {
    return !isContentVisible(element.onClass, showContent);
  }

  if (element.feature === "hidden" && element.onClass) {
    return !isContentVisible(element.onClass, showContent);
  }

  if (element.onClass && element.hidden === true) {
    return !isContentVisible(element.onClass, showContent);
  }

  return false;
};

const collectFromCols = (
  cols: Record<string, ICol>,
  uuids: Set<string>,
  options: CollectActiveCellUuidsOptions,
) => {
  for (const [uuid, col] of Object.entries(cols)) {
    if (!col || typeof col !== "object") continue;
    if (shouldHideElement(col, options.showContent)) continue;
    uuids.add(uuid);
    collectNestedColUuids(col, uuids, options);
  }
};

const collectFromGuernseyContent = (
  content: Record<string, unknown>,
  uuids: Set<string>,
  options: CollectActiveCellUuidsOptions,
) => {
  for (const [key, rows] of Object.entries(content)) {
    if (key === "cols" || !Array.isArray(rows)) continue;
    for (const item of rows) {
      if (item?.cols) {
        collectFromCols(item.cols as Record<string, ICol>, uuids, options);
      }
    }
  }
};

const collectNestedColUuids = (
  col: ICol,
  uuids: Set<string>,
  options: CollectActiveCellUuidsOptions,
) => {
  if (col.unit === "sub" && col.cols) {
    const nestedCols =
      typeof col.cols === "object" && !Array.isArray(col.cols)
        ? (col.cols as Record<string, ICol>)
        : null;
    if (nestedCols) collectFromCols(nestedCols, uuids, options);
  }

  if (col.content && typeof col.content === "object" && "cols" in col.content) {
    const contentCols = (col.content as { cols?: Record<string, ICol> }).cols;
    if (contentCols) collectFromCols(contentCols, uuids, options);
  }

  if (col.unit === "guernseyMulti" && col.content) {
    collectFromGuernseyContent(
      col.content as Record<string, unknown>,
      uuids,
      options,
    );
  }
};

const collectFromRow = (
  row: Record<string, any>,
  uuids: Set<string>,
  options: CollectActiveCellUuidsOptions,
) => {
  if (row.cols) {
    collectFromCols(row.cols as Record<string, ICol>, uuids, options);
  }
  if (Array.isArray(row.subrows)) {
    for (const subrow of row.subrows) {
      if (subrow?.cols) {
        collectFromCols(subrow.cols as Record<string, ICol>, uuids, options);
      }
    }
  }
  if (row.items) {
    for (const item of Object.values(row.items) as any[]) {
      if (item?.cols) {
        collectFromCols(item.cols as Record<string, ICol>, uuids, options);
      }
    }
  }
};

const collectFromSection = (
  section: ISection,
  uuids: Set<string>,
  options: CollectActiveCellUuidsOptions,
) => {
  if (shouldHideElement(section, options.showContent)) return;

  if (section.subSections) {
    for (const sub of section.subSections) {
      collectFromSection(sub as unknown as ISection, uuids, options);
    }
    return;
  }

  if (section.shared && section.sections) {
    for (const nested of section.sections) {
      collectFromSection(nested, uuids, options);
    }
    return;
  }

  if (!Array.isArray(section.rows)) return;

  const rows = section.rows as Record<string, any>[];
  const shouldSkipBlank = Boolean(options.excludeBlankRows) && rows.length > 1;

  for (const row of rows) {
    if (shouldSkipBlank && isBlankRow(row)) continue;
    collectFromRow(row, uuids, options);
  }
};

/**
 * Collects cell UUIDs currently present in the logbook Redux structure.
 * Used to scope react-hook-form validation to active fields only.
 */
export const collectActiveCellUuids = (
  sections: ISection[] | undefined,
  options: CollectActiveCellUuidsOptions = {},
): Set<string> => {
  const uuids = new Set<string>();
  if (!sections) return uuids;

  for (const section of sections) {
    collectFromSection(section, uuids, options);
  }

  return uuids;
};

/**
 * Returns true when a react-hook-form field name belongs to an active cell UUID.
 */
export const isActiveFieldName = (
  fieldName: string,
  activeUuids: Set<string>,
): boolean => {
  if (fieldName.startsWith(OVERLAY_PREF)) return false;

  for (const uuid of activeUuids) {
    if (
      fieldName === uuid ||
      fieldName.startsWith(`${uuid}-`) ||
      fieldName.startsWith(`${uuid}.`)
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Filters form field names to those tied to the current lbentry structure.
 */
export const getActiveFieldNamesForValidation = (
  formValues: Record<string, unknown>,
  sections: ISection[] | undefined,
  options: CollectActiveCellUuidsOptions = {},
): string[] => {
  const activeUuids = collectActiveCellUuids(sections, options);

  return Object.keys(formValues).filter(key =>
    isActiveFieldName(key, activeUuids),
  );
};

/**
 * Returns form keys that are registered in RHF but not tied to any active cell.
 * Useful for diagnosing ghost-field validation failures after row add/delete.
 */
export const getOrphanFormFieldKeys = (
  formValues: Record<string, unknown>,
  sections: ISection[] | undefined,
  options: CollectActiveCellUuidsOptions = {},
): string[] => {
  const activeUuids = collectActiveCellUuids(sections, options);

  return Object.keys(formValues).filter(
    key =>
      !key.startsWith(OVERLAY_PREF) && !isActiveFieldName(key, activeUuids),
  );
};

/**
 * Returns react-hook-form field names that belong to a single row's cells.
 */
export const getFieldNamesForRow = (
  formValues: Record<string, unknown>,
  row: Record<string, any>,
): string[] => {
  const rowUuids = collectActiveCellUuids([
    { className: "", title: "", colcount: 0, headers: [], rows: [row] },
  ]);

  return Object.keys(formValues).filter(key =>
    isActiveFieldName(key, rowUuids),
  );
};

/**
 * Unregisters react-hook-form fields tied to a row that is being removed or replaced.
 */
export const unregisterRowFormFields = (
  formValues: Record<string, unknown>,
  row: Record<string, any>,
  unregister: (name: string) => void,
) => {
  getFieldNamesForRow(formValues, row).forEach(name => unregister(name));
};

const findColByUuid = (
  sections: ISection[] | undefined,
  uuid: string,
): ICol | undefined => {
  if (!sections) return undefined;

  let found: ICol | undefined;

  const searchCols = (cols: Record<string, ICol>) => {
    if (found) return;
    for (const [colUuid, col] of Object.entries(cols)) {
      if (colUuid === uuid) {
        found = col;
        return;
      }
      if (!col || typeof col !== "object") continue;
      if (col.unit === "sub" && col.cols && typeof col.cols === "object") {
        searchCols(col.cols as Record<string, ICol>);
      }
      if (
        col.content &&
        typeof col.content === "object" &&
        "cols" in col.content
      ) {
        searchCols((col.content as { cols?: Record<string, ICol> }).cols ?? {});
      }
    }
  };

  const searchRow = (row: Record<string, any>) => {
    if (found) return;
    if (row.cols) searchCols(row.cols as Record<string, ICol>);
    if (Array.isArray(row.subrows)) {
      row.subrows.forEach((subrow: any) => searchRow(subrow));
    }
    if (row.items) {
      Object.values(row.items).forEach((item: any) => searchRow(item));
    }
  };

  const searchSection = (section: ISection) => {
    if (found) return;
    if (Array.isArray(section.rows)) {
      section.rows.forEach((row: any) => searchRow(row));
    }
    section.subSections?.forEach((sub: any) => searchSection(sub));
    section.sections?.forEach((nested: any) => searchSection(nested));
  };

  sections.forEach(searchSection);
  return found;
};

const fieldNameToCellUuid = (fieldName: string): string => {
  const exclusiveIdx = fieldName.indexOf("-exclusive-");
  if (exclusiveIdx > 0) return fieldName.slice(0, exclusiveIdx);
  const exclusiveUserIdx = fieldName.indexOf("-exclusiveUser-");
  if (exclusiveUserIdx > 0) return fieldName.slice(0, exclusiveUserIdx);
  const dotIdx = fieldName.indexOf(".");
  if (dotIdx > 0) return fieldName.slice(0, dotIdx);
  return fieldName;
};

/**
 * Returns active field names that currently have validation errors.
 */
export const getInvalidActiveFieldNames = (
  errors: Record<string, unknown>,
  activeFieldNames: string[],
): string[] => {
  const activeSet = new Set(activeFieldNames);
  return Object.keys(errors).filter(
    key =>
      activeSet.has(key) ||
      activeFieldNames.some(name => key.startsWith(`${name}.`)),
  );
};

/**
 * Maps field names to cell name/unit for save-validation diagnostics.
 */
export const describeFieldsForDiagnostics = (
  fieldNames: string[],
  sections: ISection[] | undefined,
): Array<{ field: string; name?: string; unit?: string }> => {
  const seen = new Set<string>();

  return fieldNames
    .map(field => {
      const uuid = fieldNameToCellUuid(field);
      if (seen.has(uuid)) return null;
      seen.add(uuid);
      const col = findColByUuid(sections, uuid);
      return {
        field,
        name: col?.name,
        unit: col?.unit,
      };
    })
    .filter((item): item is { field: string; name?: string; unit?: string } =>
      Boolean(item),
    );
};

const isBlankValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "");

/** A tank-limit failure: the column has a tankRef and a value was entered. */
export const isTankLimitField = (
  field: string,
  sections: ISection[] | undefined,
  values: Record<string, unknown>,
): boolean => {
  const col = findColByUuid(sections, fieldNameToCellUuid(field));
  return Boolean(col?.tankRef) && !isBlankValue(values[field]);
};

export const anyInvalidFieldHasTankRef = (
  fieldNames: string[],
  sections: ISection[] | undefined,
  values: Record<string, unknown>,
): boolean =>
  fieldNames.some(field => isTankLimitField(field, sections, values));
